import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { User, Client } from '../../types';
import DashboardHeader from './DashboardHeader';
import Footer from './Footer';
import UserForm from '../users/UserForm';
import UsersList from '../users/UsersList';
import './Dashboard.css';

const ClientUsersPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isUserAdmin } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [showUserForm, setShowUserForm] = useState(false);

  useEffect(() => {
    if (!isUserAdmin()) {
      navigate('/dashboard');
      return;
    }
    fetchUsers();
    fetchClients();
  }, [isUserAdmin, navigate]);

  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      const response = await api.get<User[]>('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      setClientsLoading(true);
      const response = await api.get<Client[]>('/clients');
      const active = (response.data || []).filter((c) => {
        const s = c.status as boolean | number | undefined;
        return s !== false && s !== 0;
      });
      setClients(active);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    } finally {
      setClientsLoading(false);
    }
  };

  if (!isUserAdmin()) {
    return null;
  }

  return (
    <div className="dashboard">
      <DashboardHeader
        title="Users"
        showNav
        showEmailButton
      />
      <div className="dashboard-content">
        <div className="users-section">
          <div className="section-header">
            <h2>Users</h2>
            <button
              onClick={() => setShowUserForm(!showUserForm)}
              className="btn btn-primary"
            >
              {showUserForm ? 'Hide Form' : 'Create New User'}
            </button>
          </div>
          {showUserForm && (
            <UserForm
              clients={clients}
              onSuccess={() => {
                fetchUsers();
                fetchClients();
                setShowUserForm(false);
              }}
              clientsLoading={clientsLoading}
              allowAdminRole={false}
            />
          )}
          <UsersList
            users={users}
            loading={usersLoading}
            onUserDeleted={fetchUsers}
            currentUserId={user?.id}
          />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ClientUsersPage;
